import { useMemo } from 'react';
import { FiDownload } from 'react-icons/fi';
import { ModelResults } from '../../model/domain/types';
import { ImpactTotals } from '../../impact/domain/types';
import { LifetimeProjection } from '../../lifetime/domain/types';
import { useImpactMode } from '../../impact/context/ImpactModeContext';
import { ImpactMode, ReportProjectConfig, ProfileComparisonData } from '../domain/types';
import { extractReportData } from '../domain/reportDataExtractor';
import { buildExcelWorkbook, downloadWorkbook } from '../domain/excelBuilder';

interface ExcelDownloadButtonProps {
  projectName: string;
  techType: string;
  capacityMw: number;
  location: string;
  analysisYear: number;
  isFutureProject: boolean;
  codDate?: string;
  customerName?: string;
  county?: string;
  existingProjectFlag?: boolean;
  modelResults: ModelResults;
  impactTotals: ImpactTotals;
  lifetimeProjection: LifetimeProjection | null;
  impactMode?: ImpactMode;
  latitude?: number;
  longitude?: number;
  eiaBaCode?: string;
  nercCode?: string;
  state?: string;
  profileComparison?: ProfileComparisonData;
}

export function ExcelDownloadButton({
  projectName,
  techType,
  capacityMw,
  location,
  analysisYear,
  isFutureProject,
  codDate,
  customerName,
  county,
  existingProjectFlag,
  modelResults,
  impactTotals,
  lifetimeProjection,
  impactMode: impactModeProp,
  latitude,
  longitude,
  eiaBaCode,
  nercCode,
  state,
  profileComparison,
}: ExcelDownloadButtonProps) {
  const { mode: contextMode } = useImpactMode();
  const impactMode: ImpactMode = impactModeProp ?? contextMode ?? 'ctg';

  const projectConfig = useMemo<ReportProjectConfig | undefined>(() => {
    if (latitude == null || longitude == null) return undefined;
    return { latitude, longitude, eiaBaCode, nercCode, state };
  }, [latitude, longitude, eiaBaCode, nercCode, state]);

  const handleClick = () => {
    const reportData = extractReportData({
      projectName,
      techType,
      capacityMw,
      location,
      analysisYear,
      isFutureProject,
      codDate,
      customerName,
      county,
      existingProjectFlag,
      modelResults,
      impactTotals,
      lifetimeProjection,
      impactMode,
      projectConfig,
      profileComparison,
    });

    const wb = buildExcelWorkbook(reportData);
    const safeName = projectName.replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadWorkbook(wb, `${safeName}_Impact_Report.xlsx`);
  };

  return (
    <button
      onClick={handleClick}
      className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors border border-quantum-500/50 text-quantum-400 hover:bg-quantum-500/10"
    >
      <FiDownload size={16} />
      Generate XLSX
    </button>
  );
}
